import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { compareAsc, addDays } from "date-fns";
import toast from 'react-hot-toast';
import { Group, Panel } from 'react-resizable-panels';
import { 
    useMediaQuery, 
    useTheme, 
    CircularProgress, 
    Box, 
    Typography,  
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select, 
} from '@mui/material';
import {
    UnfoldMore,
    UnfoldLess,
} from "@mui/icons-material";
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { filterCrewMember } from 'state/helpers';
import ErrorBoundary from 'components/ErrorBoundary';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import CrewFilterDialog from 'components/CrewFilterDialog';
import CrewGanttChart from './dhtmlx';


const usePressAndHold = (action) => {
    const timerRef = useRef(null);

    const stop = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    const start = useCallback(() => {
        action();
        let delay = 150;
        const schedule = () => {
            timerRef.current = setTimeout(() => {
                action();
                delay = Math.max(30, Math.floor(delay * 0.8));
                schedule();
            }, delay);
        };
        timerRef.current = setTimeout(schedule, 400);
    }, [action]);

    return { onMouseDown: start, onMouseUp: stop, onMouseLeave: stop, onTouchStart: start, onTouchEnd: stop };
};

const GanttChart = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };

    const { startDate, endDate } = useDateRangeContext();

    const [scheduleCodes, setScheduleCodes] = useState([]);
    const [rosterDetails, setRosterDetails] = useState([]);
    
    const splitterWidth = isMobile ? 180 : 220;
    const [rowHeight, setRowHeight] = useState(52);
    const [cellWidth, setCellWidth] = useState(12);

    const crewRanksData = useSelector((state) => state.crewRanks);
    
    
    useEffect(() => {
        const fetchRosterDetails = async () => {
            if (!startDate || !endDate) return [];
            
            try {
                setError(null);
                setLoading(true);   

                const url = '/rosterdetails';
                const payload = { 
                    startDate: addDays(startDate, -1), 
                    endDate: addDays(endDate, 1) 
                };
                
                const res = await api.post(url, payload);
                
                if (res.data) {        
                    const filteredData = res?.data?.filter((roster) => {            
                        const flightDateToCompare =
                        timeMode === 'UTC' ? roster.flightDate : roster.flightDateLocal;
                    
                        return (
                            compareAsc(flightDateToCompare, startDate) >= 0 &&
                            compareAsc(flightDateToCompare, endDate) <= 0
                        );
                    });
                    
                    setRosterDetails(filteredData);  
                }
            } catch (error) {
                setError(error);
                toast.error(error?.message || "Failed to fetch roster details!");
            } finally {
                setLoading(false);
            }
        };
      
        fetchRosterDetails();
    }, [timeMode, startDate, endDate]); 

    const filteredRosterDetails = useMemo(() => {
        if (!Array.isArray(rosterDetails)) return [];
        
        let result = rosterDetails;
        
        if (filters) {
            result = result.filter(r => filterCrewMember(r, filters));
        }
    
        if (scheduleCodes?.length > 0) {
            result = result.filter(r => scheduleCodes.includes(r.flightNo));
        }
    
        return result;
    }, [rosterDetails, filters, scheduleCodes]);

    const decreaseRowHeight = useCallback(() => setRowHeight(prev => Math.max(1, prev - 1)), []);
    const increaseRowHeight = useCallback(() => setRowHeight(prev => prev + 1), []);
    const decreaseCellWidth = useCallback(() => setCellWidth(prev => Math.max(1, prev - 1)), []);
    const increaseCellWidth = useCallback(() => setCellWidth(prev => prev + 1), []);

    const holdDecreaseRowHeight = usePressAndHold(decreaseRowHeight);
    const holdIncreaseRowHeight = usePressAndHold(increaseRowHeight);
    const holdDecreaseCellWidth = usePressAndHold(decreaseCellWidth);
    const holdIncreaseCellWidth = usePressAndHold(increaseCellWidth);


    const handleFilterOpen = useCallback(() => setIsFilterDialogOpen(true), []);
    const handleFilterClose = useCallback(() => setIsFilterDialogOpen(false), []);
    const handleFilterApply = useCallback((filterValues) => setFilters(filterValues), []);
    
    const {
        baseOptions,
        postOptions,
        trainingQualOptions,
        scheduleCodeOptions,
    } = useMemo(() => {
        if (!Array.isArray(rosterDetails) || rosterDetails.length === 0) {
            return {
                baseOptions: [],
                postOptions: [],
                trainingQualOptions: [],
                scheduleCodeOptions: [],
            };
        }

        /* ---------- BASE ---------- */
        const baseOptions = Array.from(
            new Set(rosterDetails.map(m => m.base).filter(Boolean))
        ).sort();

        /* ---------- POST / RANK ---------- */
        const postOptions = Array.from(
            new Set(rosterDetails.map(m => m.post).filter(Boolean))
        )
        .map(post => crewRanksData.find(r => r.rankCode === post))
        .filter(Boolean)
        .sort((a, b) => a.rankPriority - b.rankPriority)
        .map(r => r.rankCode);

        /* ---------- TRAINING QUAL ---------- */
        const trainingQualOptions = Array.from(
            new Set(
                rosterDetails.flatMap(m => m.trainingQuals ?? [])
            )
        ).sort();

        /* ---------- SCHEDULE CODE ---------- */
        const scheduleCodeOptions = Array.from(
            new Map(rosterDetails
                .filter(r => !r.routeNo && r.flightNo)
                .map(r => [r.flightNo, r])
            ).values()
        )
        .sort((a, b) => {
            const catA = a.category ?? "";
            const catB = b.category ?? "";

            if (catA !== catB) {
                return catA.localeCompare(catB);
            }

            return (a.flightNo ?? "").localeCompare(b.flightNo ?? "");
        })
        .map(r => ({
            scheduleCode: r.flightNo,
            description: r.description,
        }));

        return {
            baseOptions,
            postOptions,
            trainingQualOptions,
            scheduleCodeOptions,
        };
    }, [rosterDetails, crewRanksData]);


    
    return (
        <Box className="main-container-box">              
            <FlexBetween>
                <Header 
                    title="Crew Rosters" 
                    subtitle={`${loading ? 'Loading...' : 'Gantt Chart'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>   
                        {!isMobile && (
                            <Box display="flex" alignItems="center" gap={1}> 
                                <FormControl
                                    sx={{ width: isMobile ? "6rem" : "12rem" }}
                                    size="small"
                                >
                                    <InputLabel id="schedule-codes-label">Schedule Codes</InputLabel>
                                    <Select
                                        multiple
                                        label="Schedule Codes"
                                        value={scheduleCodes}
                                        onChange={(e) => setScheduleCodes(e.target.value)}
                                    >
                                        {scheduleCodeOptions?.map((item, index) => {
                                            return (
                                            <MenuItem key={index} value={item.scheduleCode}>
                                                {item.scheduleCode} {item.description ? ` - ${item.description}` : ""}
                                            </MenuItem>
                                            )
                                        })}
                                    </Select>
                                </FormControl>
                                

                            </Box>                             
                        )}                        
                        
                        <StyledButton {...holdDecreaseRowHeight}><UnfoldLess /></StyledButton>
                        <StyledButton {...holdIncreaseRowHeight}><UnfoldMore /></StyledButton>
                        <StyledButton {...holdDecreaseCellWidth} sx={{ transform: 'rotate(90deg)' }}>
                            <UnfoldLess />
                        </StyledButton>
                        <StyledButton {...holdIncreaseCellWidth} sx={{ transform: 'rotate(90deg)' }}>
                            <UnfoldMore />
                        </StyledButton>
                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>             
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <ErrorBoundary fallback={<div>Oops! Something failed.</div>}>
                        <Box
                            height={LAYOUT_HEIGHT}  
                            sx={{ gridColumn: "span 12", overflowY: 'hidden' }}    
                        >     
                            <Group orientation="vertical" style={{ height: '100%' }}>                               
                                <Panel defaultSize={100} minSize={0}>
                                    <CrewGanttChart
                                        rowHeight={rowHeight}
                                        cellWidth={cellWidth}
                                        splitterWidth={splitterWidth}
                                        rosterDetails={filteredRosterDetails}
                                    />
                                </Panel>
                               
                                <Panel 
                                    defaultSize={0.001} 
                                    minSize={0} 
                                    maxSize={0}
                                    collapsible={false}
                                >
                                    <Box sx={{ height: '100%', pointerEvents: 'none' }}>
                                        {/* TEMPO BLANK HERE */}
                                    </Box>
                                </Panel>
                            </Group>

                            {isFilterDialogOpen &&
                                <CrewFilterDialog
                                    open={isFilterDialogOpen}
                                    onClose={handleFilterClose}
                                    onApply={handleFilterApply}
                                    postOptions={postOptions}
                                    baseOptions={baseOptions}
                                    trainingQualOptions={trainingQualOptions}
                                    currentFilters={filters}
                                />
                            }                    
                        </Box>
                    </ErrorBoundary>                    
                </Box>                              
            )}
        </Box>
    );
};

export default GanttChart;
